import { NotificationData } from '../../services/notification.service';
import { readSubscriptionPause } from './subscription-pause.util';
import { isWayRedeemedNotificationRow } from './way-redeem-notification.util';

export function resolveNotificationGateType(
  data: Record<string, any> | undefined
): 'automatic' | 'manual' | null {
  const gateType = data?.['gate_type'];
  if (gateType === 'automatic' || gateType === 'manual') {
    return gateType;
  }
  return null;
}

export function resolveNotificationLaneId(data: Record<string, any> | undefined): string {
  const laneId = data?.['lane_id'];
  if (typeof laneId === 'string' || typeof laneId === 'number') {
    return String(laneId).trim();
  }
  return '';
}

export function resolveNotificationCameraRoi(data: Record<string, any> | undefined): number | null {
  const raw = data?.['camera_roi'] ?? data?.['roiID'] ?? data?.['roiId'];
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return null;
  }
  const roi = Number(raw);
  return Number.isInteger(roi) ? roi : null;
}

/**
 * When a lane is selected, only show LPR notifications / popups for that lane.
 * Prefer lane_id match; fall back to camera ROI. Events without lane metadata are hidden
 * once a selected lane is set (lane_id is required on lpr_entries going forward).
 */
export function notificationMatchesSelectedLane(
  data: Record<string, any> | undefined,
  selectedLaneId?: string | null,
  selectedCameraRoi?: number | null
): boolean {
  const laneId = selectedLaneId != null ? String(selectedLaneId).trim() : '';
  if (!laneId && (selectedCameraRoi == null || !Number.isInteger(selectedCameraRoi))) {
    return true;
  }

  const eventLaneId = resolveNotificationLaneId(data);
  if (laneId && eventLaneId) {
    return eventLaneId === laneId;
  }

  const eventRoi = resolveNotificationCameraRoi(data);
  if (selectedCameraRoi != null && Number.isInteger(selectedCameraRoi) && eventRoi != null) {
    return eventRoi === selectedCameraRoi;
  }

  // Selected lane is set but event has no usable lane/ROI — do not show popup.
  return false;
}

export function resolveNotificationLocationId(data: Record<string, any> | undefined): string {
  const locationId = data?.['location_id'] ?? data?.['locationId'];
  if (typeof locationId === 'string' || typeof locationId === 'number') {
    return String(locationId).trim();
  }
  return '';
}

/** Lane has a configured gate (backend only attaches lane context when gate_serial + gate_relay exist). */
export function hasOperableLaneGate(data: Record<string, any> | undefined): boolean {
  return !!resolveNotificationLocationId(data) && !!resolveNotificationLaneId(data);
}

/**
 * A paused membership read (CWS-1544) is shown in the Active Subscription popup but grants no
 * wash: the lane gate must never open for it, automatically or from the popup.
 */
function isPausedMembershipRead(data: Record<string, any> | undefined): boolean {
  return (
    readSubscriptionPause(data?.['pause'])?.state === 'paused' ||
    data?.['refusal_reason'] === 'subscription_paused' ||
    Array.isArray(data?.['paused_options'])
  );
}

export function isAutomaticGateActiveSubscription(
  notification: NotificationData | null | undefined
): boolean {
  if (notification?.type !== 'active-subscription') {
    return false;
  }
  const data = notification.data as Record<string, any> | undefined;
  if (isPausedMembershipRead(data)) {
    return false;
  }
  return resolveNotificationGateType(data) === 'automatic' && hasOperableLaneGate(data);
}

export function isManualGateActiveSubscription(
  notification: NotificationData | null | undefined
): boolean {
  if (notification?.type !== 'active-subscription') {
    return false;
  }
  const data = notification.data as Record<string, any> | undefined;
  if (isPausedMembershipRead(data)) {
    return false;
  }
  return resolveNotificationGateType(data) === 'manual' && hasOperableLaneGate(data);
}

/** Lane record shape from `GET location/view-location/:id/lanes` (gate metadata subset). */
export interface LaneGateRecord {
  id?: string | number | null;
  status?: string | null;
  gate_type?: string | null;
  gate_serial?: string | null;
  gate_relay?: number | null;
}

/**
 * A lane is a manual, operable gate when it is active, its gate_type is `manual`,
 * and it is wired with a gate serial + relay. Automatic gates open on their own
 * (LPR/auto path) and lanes without a gate cannot be opened.
 */
export function isManualOperableLane(lane: LaneGateRecord | null | undefined): boolean {
  if (!lane) {
    return false;
  }
  const isActive = String(lane.status ?? 'active').toLowerCase() === 'active';
  const isManual = String(lane.gate_type ?? '').toLowerCase() === 'manual';
  const hasSerial = !!String(lane.gate_serial ?? '').trim();
  const hasRelay = lane.gate_relay != null;
  return isActive && isManual && hasSerial && hasRelay;
}

/** Find a lane by id within a lanes list and report whether it is a manual operable gate. */
export function findManualOperableLane(
  lanes: LaneGateRecord[] | null | undefined,
  laneId: string | number | null | undefined
): LaneGateRecord | null {
  const id = laneId != null ? String(laneId).trim() : '';
  if (!id || !Array.isArray(lanes)) {
    return null;
  }
  const match = lanes.find(lane => String(lane?.id ?? '').trim() === id) ?? null;
  return match && isManualOperableLane(match) ? match : null;
}

function isNonWayAutoRedeemActiveSubscription(
  notification: NotificationData | null | undefined
): boolean {
  if (notification?.type !== 'active-subscription') {
    return false;
  }
  const data = notification.data as Record<string, any> | undefined;
  if (!data) {
    return false;
  }
  const hasAutoRedeemFlag = data['auto_redeem'] === true || data['gate_opened'] === true;
  return hasAutoRedeemFlag && !isWayRedeemedNotificationRow(data);
}

/**
 * LPR/image membership purchase entitlement SSE (`notifyLprMembershipSubscriptionActivated`).
 * First wash is already redeemed server-side; payment-success covers attendant UX — skip Active
 * subscription modal for this event.
 */
export function isPostPurchaseLprMembershipActivation(
  notification: NotificationData | null | undefined
): boolean {
  if (notification?.type !== 'active-subscription') {
    return false;
  }
  const data = notification.data as Record<string, any> | undefined;
  // Only skip when the first wash actually redeemed — activation-without-redeem
  // still needs the Active subscription attendant message.
  return data?.['subscription_activated'] === true && data?.['first_wash_redeemed'] === true;
}

/**
 * Membership already redeemed from the multi-eligible chooser — Active subscription would
 * duplicate the confirm. Pusher still emits active-subscription for gate/history; skip the modal.
 */
export function isMultiEligibleMembershipRedeemCompleted(
  notification: NotificationData | null | undefined
): boolean {
  if (notification?.type !== 'active-subscription') {
    return false;
  }
  const data = notification.data as Record<string, any> | undefined;
  const sourceEvents = data?.['source_events'];
  if (!Array.isArray(sourceEvents)) {
    return false;
  }
  return sourceEvents.some(
    (event: unknown) => String(event ?? '').trim() === 'multi-eligible-selection'
  );
}

/** Automatic gate + membership: skip popup and open gate directly. */
export function shouldSkipActiveSubscriptionModal(
  notification: NotificationData | null | undefined
): boolean {
  if (isPostPurchaseLprMembershipActivation(notification)) {
    return true;
  }
  if (isMultiEligibleMembershipRedeemCompleted(notification)) {
    return true;
  }
  if (!isAutomaticGateActiveSubscription(notification)) {
    return false;
  }
  // Vendor (DRB, etc.) auto-redeem already opened the gate server-side — show modal.
  if (isNonWayAutoRedeemActiveSubscription(notification)) {
    return false;
  }
  return true;
}

/** Manual gate + membership: show "Open Gate" and allow attendant to trigger open. */
export function canOpenGateForActiveSubscription(
  notification: NotificationData | null | undefined
): boolean {
  return isManualGateActiveSubscription(notification);
}

function isWayPurchaseDetailNotification(
  notification: NotificationData | null | undefined
): boolean {
  return (
    notification?.type === 'way-single-wash' || notification?.type === 'way-active-subscription'
  );
}

/**
 * Gateless/manual-lane Way auto-redeem with redemption_completed: staff open the gate
 * manually when the lane has gate_type manual and lane routing metadata is present.
 */
export function canOpenGateForWayPurchaseNotification(
  notification: NotificationData | null | undefined
): boolean {
  if (!isWayPurchaseDetailNotification(notification)) {
    return false;
  }
  const data = notification?.data as Record<string, any> | undefined;
  if (data?.['redemption_completed'] !== true) {
    return false;
  }
  return resolveNotificationGateType(data) === 'manual' && hasOperableLaneGate(data);
}
